import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView } from '@/card/record'
import type { Locale } from '@/i18n'
import { ApiError } from '@/lib/api'
import { formatDate, formatNumber } from '@/lib/format'
import { AdminPage, AdminState, Notice, PrimaryButton, SkeletonTable, isForbidden } from '@/admin/AdminShell'
import {
  MODEL_KEY,
  RESOURCE,
  fetchModelReport,
  trainModel,
  type Coefficient,
  type ModelReport,
  type Scores,
} from './forecastApi'

type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * Качество модели прогноза сделок (#128): «качество модели на отложенной выборке отражено в
 * отчёте».
 *
 * ## Что на экране и почему именно это
 *
 * - **Метрики модели рядом с базовой линией.** Базовая линия — статическая вероятность стадии,
 *   та, что на доске сегодня. Модель стоит показывать ровно настолько, насколько она лучше; AUC
 *   без сравнения не говорит ничего.
 * - **Отложенная выборка — последние по дате закрытия сделки**, а не случайные: так отвечается
 *   вопрос, ради которого прогноз нужен, — что будет со сделками, открытыми сейчас.
 * - **Калибровка по корзинам**: модель может хорошо ранжировать и при этом говорить 90 % там,
 *   где выигрывают половину. Пустая корзина — «—», а не ноль.
 * - **Мало данных — словами**, с порогом и числом закрытых сделок: прогноза нет, и экран
 *   говорит почему, а не показывает пустые таблицы.
 */
export function ForecastModelPage() {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const report = useQuery({
    queryKey: MODEL_KEY,
    queryFn: ({ signal }) => fetchModelReport(signal),
    retry: false,
  })
  const train = useMutation({
    mutationFn: () => trainModel(),
    onSuccess: (data) => {
      queryClient.setQueryData(MODEL_KEY, data)
      // The board and the cards show forecasts of the previous model until reread.
      void queryClient.invalidateQueries({ queryKey: ['deal-board'] })
      void queryClient.invalidateQueries({ queryKey: ['deal-card'] })
    },
  })
  const mayTrain = can(user, RESOURCE, 'update')

  return (
    <AdminPage
      sectionKey="nav.section.sales"
      titleKey="nav.forecastModel"
      leadKey="forecast.model.lead"
      forbidden={isForbidden(report.error)}
      primary={
        mayTrain && (
          <PrimaryButton
            icon="refresh"
            label={train.isPending ? t('forecast.model.training') : t('forecast.model.train')}
            onClick={() => train.mutate()}
          />
        )
      }
    >
      <Notice text={train.error ? (train.error instanceof ApiError ? train.error.message : String(train.error)) : null} />
      <AdminState
        isLoading={report.isLoading}
        error={report.error}
        onRetry={() => void report.refetch()}
        forbiddenTitleKey="forecast.model.forbidden"
        skeleton={<SkeletonTable columns={3} rows={4} />}
      >
        {report.data && <Report report={report.data} t={t} locale={locale} />}
      </AdminState>
    </AdminPage>
  )
}

function Report({ report, t, locale }: { report: ModelReport; t: Translate; locale: Locale }) {
  if (report.status === 'none') {
    return (
      <StateView boxed kind="empty" title={t('forecast.model.none.title')}
                 description={t('forecast.model.none.text', { min: report.configuredMinClosedDeals })} />
    )
  }
  if (report.status === 'insufficient_data' || report.metrics == null) {
    return (
      <StateView boxed kind="empty" title={t('forecast.model.insufficient.title')}
                 description={t('forecast.model.insufficient.text', {
                   message: report.message ?? '',
                   date: report.trainedAt ? formatDate(report.trainedAt.slice(0, 10), locale) : '—',
                 })} />
    )
  }
  const metrics = report.metrics
  return (
    <div className="stack" style={{ gap: 16 }}>
      <p data-testid="forecast-model-summary">
        {t('forecast.model.summary', {
          date: report.trainedAt ? formatDate(report.trainedAt.slice(0, 10), locale) : '—',
          train: formatNumber(report.trainDeals ?? 0, locale),
          trainExamples: formatNumber(report.trainExamples ?? 0, locale),
          holdout: formatNumber(report.holdoutDeals ?? 0, locale),
          holdoutExamples: formatNumber(report.holdoutExamples ?? 0, locale),
          from: report.holdoutFrom ? formatDate(report.holdoutFrom.slice(0, 10), locale) : '—',
          deals: formatNumber(report.forecastDeals, locale),
        })}
      </p>

      <section className="stack" style={{ gap: 8 }} aria-labelledby="fm-quality-h">
        <h2 id="fm-quality-h" className="h2">{t('forecast.model.quality')}</h2>
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('forecast.model.quality')}>
            <thead>
              <tr>
                <th>{t('forecast.model.col.metric')}</th>
                <th className="num">{t('forecast.model.col.model')}</th>
                <th className="num">{t('forecast.model.col.baseline')}</th>
              </tr>
            </thead>
            <tbody>
              <MetricRow name={t('forecast.model.auc')} hint={t('forecast.model.aucHint')}
                         model={metrics.model} baseline={metrics.baseline} pick={(s) => s.auc} t={t} />
              <MetricRow name={t('forecast.model.logLoss')} hint={t('forecast.model.lowerBetter')}
                         model={metrics.model} baseline={metrics.baseline} pick={(s) => s.logLoss} t={t} />
              <MetricRow name={t('forecast.model.brier')} hint={t('forecast.model.lowerBetter')}
                         model={metrics.model} baseline={metrics.baseline} pick={(s) => s.brier} t={t} />
            </tbody>
          </table>
        </div>
        <p className="t-sm secondary">{t('forecast.model.snapshot')}</p>
      </section>

      <section className="stack" style={{ gap: 8 }} aria-labelledby="fm-calibration-h">
        <h2 id="fm-calibration-h" className="h2">{t('forecast.model.calibration')}</h2>
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('forecast.model.calibration')}>
            <thead>
              <tr>
                <th>{t('forecast.model.col.bucket')}</th>
                <th className="num">{t('forecast.model.col.examples')}</th>
                <th className="num">{t('forecast.model.col.predicted')}</th>
                <th className="num">{t('forecast.model.col.actual')}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.calibration.map((bucket) => (
                <tr key={bucket.from}>
                  <td>{`${percent(bucket.from)}–${percent(bucket.to)}`}</td>
                  <td className="num">{formatNumber(bucket.count, locale)}</td>
                  <td className="num">{bucket.meanPredicted == null ? '—' : percent(bucket.meanPredicted)}</td>
                  <td className="num">{bucket.actualRate == null ? '—' : percent(bucket.actualRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="t-sm secondary">
          {t('forecast.model.winRates', {
            train: percent(metrics.trainWinRate),
            holdout: percent(metrics.holdoutWinRate),
          })}
        </p>
      </section>

      <section className="stack" style={{ gap: 8 }} aria-labelledby="fm-coefficients-h">
        <h2 id="fm-coefficients-h" className="h2">{t('forecast.model.coefficients')}</h2>
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('forecast.model.coefficients')}>
            <thead>
              <tr>
                <th>{t('forecast.model.col.feature')}</th>
                <th className="num">{t('forecast.model.col.weight')}</th>
                <th>{t('forecast.model.col.direction')}</th>
              </tr>
            </thead>
            <tbody>
              {report.coefficients.map((coefficient) => (
                <tr key={coefficient.code}>
                  <td>{featureName(t, coefficient)}</td>
                  <td className="num">{coefficient.weight.toFixed(2)}</td>
                  <td>{t(coefficient.weight >= 0 ? 'forecast.effect.up' : 'forecast.effect.down')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function MetricRow({
  name,
  hint,
  model,
  baseline,
  pick,
  t,
}: {
  name: string
  hint: string
  model: Scores
  baseline: Scores
  pick: (scores: Scores) => number | null | undefined
  t: Translate
}) {
  const show = (value: number | null | undefined) => (value == null ? t('forecast.model.undefined') : value.toFixed(3))
  return (
    <tr>
      <td title={hint}>{name}</td>
      <td className="num strong">{show(pick(model))}</td>
      <td className="num">{show(pick(baseline))}</td>
    </tr>
  )
}

function percent(value: number): string {
  return `${Math.round(value * 100)} %`
}

/** Название признака; индикатор источника лида — «Источник лида: website». */
function featureName(t: Translate, coefficient: Coefficient): string {
  const [group, value] = coefficient.code.split(':')
  if (value !== undefined) {
    return t(`forecast.feature.${group}`, { value })
  }
  return t(`forecast.feature.${coefficient.code}`)
}
